/**
 * =============================================================================
 * EmptyState — B-6 shared primitive (ADR-025 §6.2; design-v3 §F)
 * =============================================================================
 *
 * EXTRACTED from the three shipped empty-state idioms; each variant reproduces
 * its incumbent byte-for-byte (Box props, testid placement, text colors):
 *
 *   plain     Box(padding md)            > Text[testid]           — queue/media
 *   centered  Box(padding lg, centered)[testid] > Text bold sec + Text sec
 *                                                                 — replies/RFI
 *   note      Box(marginTop md)[testid]  > Text secondary         — audit
 *
 * Render CONDITIONS stay at every call site (the tri-condition
 * !loading && !error && empty is behavior, not presentation). Bare-<Text>
 * empty branches (QuestionChangesTimeline, QuestionWorkspace images) are
 * deliberately NOT consumers — wrapping them would change the DOM
 * (design-v3 §F / adjudication CL-8).
 * =============================================================================
 */

import React from 'react';
import { Box, Text } from '@bigcommerce/big-design';

export interface EmptyStateProps {
  variant: 'plain' | 'centered' | 'note';
  /** Bold headline — centered variant only. */
  title?: string;
  body: React.ReactNode;
  testId?: string;
}

export function EmptyState({ variant, title, body, testId }: EmptyStateProps): React.ReactElement {
  if (variant === 'plain') {
    return (
      <Box padding="medium">
        <Text data-testid={testId}>{body}</Text>
      </Box>
    );
  }
  if (variant === 'centered') {
    return (
      <Box padding="large" style={{ textAlign: 'center' }} data-testid={testId}>
        <Text bold color="secondary">
          {title}
        </Text>
        <Text color="secondary">{body}</Text>
      </Box>
    );
  }
  return (
    <Box marginTop="medium" data-testid={testId}>
      <Text color="secondary">{body}</Text>
    </Box>
  );
}
